import React, { useEffect, useMemo, useState } from "react";
import { Box, Paper, Button, Typography, LinearProgress } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import { evaluateForm } from "../engine/logicEngine";
import FieldInput from "./FieldInput";

const INFO_TYPES = ["section", "paragraph", "heading", "cover"];

/**
 * Renderizador estilo "Card" do JotForm: uma pergunta por vez, em tela cheia,
 * avançando com "Próximo". Respeita visibilidade condicional, skip e cálculos.
 */
export default function CardRenderer({ definition, values, onChange, onSubmit, submitting, title, description, embedded, onCheckApa, submitError }) {
  const evaluation = useMemo(() => evaluateForm(definition, values || {}), [definition, values]);
  const fieldsById = useMemo(
    () => Object.fromEntries(((definition && definition.fields) || []).map((f) => [f.id, f])),
    [definition]
  );

  // sequência de campos visíveis (ordem das páginas), excluindo páginas puladas
  const sequence = useMemo(() => {
    const seq = [];
    ((definition && definition.pages) || []).forEach((page) => {
      const ids = page.fieldIds || [];
      const allSkipped = ids.length > 0 && ids.every((id) => evaluation.skippedFieldIds.includes(id));
      if (allSkipped) return;
      ids.forEach((id) => {
        const f = fieldsById[id];
        if (!f) return;
        if (evaluation.skippedFieldIds.includes(id)) return;
        if (!evaluation.visible[id]) return;
        seq.push(id);
      });
    });
    return seq;
  }, [definition, fieldsById, evaluation]);

  const [currentId, setCurrentId] = useState(null);
  const [showError, setShowError] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState(null);
  // Valor digitado que o campo não consegue gravar (data incompleta, medida fora
  // da faixa). Não vira erro do motor: lá o campo aparece só como vazio.
  const [entradaInvalida, setEntradaInvalida] = useState("");
  const [direction, setDirection] = useState("next");

  // reflete no valor exibido campos calculados (ex.: idade a partir da data de nascimento)
  useEffect(() => {
    const targets = evaluation.targets || {};
    Object.keys(targets).forEach((fid) => {
      if ((values || {})[fid] !== targets[fid]) onChange && onChange(fid, targets[fid]);
    });
  }, [evaluation.targets, values, onChange]);

  if (sequence.length === 0) {
    return <Typography color="text.secondary" sx={{ p: 4, textAlign: "center" }}>Formulário sem campos.</Typography>;
  }

  const current = currentId && sequence.includes(currentId) ? currentId : sequence[0];
  const idx = sequence.indexOf(current);
  const field = fieldsById[current];
  const isLast = idx >= sequence.length - 1;
  const isInfo = INFO_TYPES.includes(field.type);
  const fieldError = (showError ? evaluation.errors[current] : null) || checkError;

  async function goNext() {
    if (!isInfo && evaluation.errors[current]) { setShowError(true); return; }
    if (entradaInvalida) { setShowError(false); setCheckError(entradaInvalida); return; }
    setShowError(false);
    setCheckError(null);
    // Gate APA: ao sair do campo CPF, verifica no backend se o paciente ainda precisa responder.
    if (onCheckApa && !isInfo && field.clinicalKey === "cpf") {
      setChecking(true);
      try {
        const res = await onCheckApa(field, (values || {})[current]);
        if (res && res.proceed === false) {
          // Bloqueio terminal (já respondido / expirado) troca a tela via status no PublicForm;
          // erro não-terminal (ex.: CPF inválido) é exibido no próprio card.
          if (res.error) setCheckError(res.error);
          return;
        }
      } catch (e) {
        setCheckError("Não foi possível verificar o CPF agora. Tente novamente.");
        return;
      } finally {
        setChecking(false);
      }
    }
    if (isLast) { handleFinish(); return; }
    setDirection("next");
    setCurrentId(sequence[idx + 1]);
  }
  function goPrev() {
    setShowError(false);
    setCheckError(null);
    setEntradaInvalida("");
    if (idx > 0) { setDirection("prev"); setCurrentId(sequence[idx - 1]); }
  }
  function handleFinish() {
    if (!evaluation.valid) {
      // vai para o primeiro campo com erro
      const firstErr = sequence.find((id) => evaluation.errors[id]);
      if (firstErr) { setCurrentId(firstErr); setShowError(true); return; }
      // Campo em erro que não está em página nenhuma (import de JSON, página excluída):
      // o paciente não tem como alcançá-lo, então diz qual é em vez de travar em silêncio.
      const orfao = Object.keys(evaluation.errors)[0];
      const rotulo = (fieldsById[orfao] || {}).label || orfao;
      setCheckError(`Não é possível enviar: o campo "${rotulo}" (${evaluation.errors[orfao]}) não aparece em nenhuma página. Avise o responsável pelo formulário.`);
      return;
    }
    // Campo escondido por regra (ou em página pulada) não pode ser gravado: a paciente
    // que responde "grávida: sim", preenche as semanas e depois muda para "não" deixaria
    // no prontuário uma ficha que se contradiz — e o escore é calculado sem esse valor.
    if (onSubmit) onSubmit(values || {});
  }

  // Enter avança para a próxima pergunta; Esc volta (quando há para onde voltar).
  function handleKeyDown(e) {
    const tag = (e.target.tagName || "").toLowerCase();
    if (e.key === "Enter") {
      if (tag === "textarea" || tag === "button") return; // nova linha / evita disparo duplo
      if (e.target.closest && e.target.closest(".MuiSelect-select")) return; // deixa abrir a lista
      e.preventDefault();
      goNext();
    } else if (e.key === "Escape") {
      if (document.querySelector(".MuiPopover-root, .MuiModal-root")) return; // não interfere em menu/dialog aberto
      if (idx > 0) goPrev();
    }
  }

  const progress = Math.round(((idx + 1) / sequence.length) * 100);

  return (
    <Box sx={{ minHeight: embedded ? 520 : "100vh", background: "#dfe9fb", display: "flex", flexDirection: "column" }}>
      <LinearProgress variant="determinate" value={progress} sx={{ height: 6 }} />

      <Box sx={{ textAlign: "center", pt: embedded ? 2 : 4, pb: 1 }}>
        <Typography variant={embedded ? "h6" : "h4"} sx={{ color: "#13335f", fontWeight: 600 }}>{title}</Typography>
        {description && <Typography color="text.secondary" sx={{ mt: 0.5 }}>{description}</Typography>}
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
        <Paper elevation={3} onKeyDown={handleKeyDown} sx={{ width: "100%", maxWidth: 860, borderRadius: 2 }}>
          <Box sx={{ p: { xs: 3, md: 5 }, position: "relative", zIndex: 1 }}>
            <Box key={current} sx={{
              animation: `${direction === "prev" ? "apaStepPrev" : "apaStepNext"} .5s cubic-bezier(0.16, 1, 0.3, 1) both`,
              "@keyframes apaStepNext": {
                from: { opacity: 0, transform: "translateX(56px) scale(0.98)" },
                to: { opacity: 1, transform: "translateX(0) scale(1)" },
              },
              "@keyframes apaStepPrev": {
                from: { opacity: 0, transform: "translateX(-56px) scale(0.98)" },
                to: { opacity: 1, transform: "translateX(0) scale(1)" },
              },
            }}>
              <FieldInput
                key={current}
                field={field}
                large
                required={evaluation.required[current]}
                value={(values || {})[current]}
                error={fieldError}
                onInvalid={setEntradaInvalida}
                onChange={(v) => onChange && onChange(current, v)}
              />
            </Box>
          </Box>

          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#13335f", color: "#fff", px: 3, py: 1.5,
            borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px",
          }}>
            <Button onClick={goPrev} disabled={idx === 0}
              startIcon={<ArrowBackIcon />} sx={{ color: "#cfe0ff" }}>
              Anterior
            </Button>

            {(fieldError || submitError) && (
              <Typography variant="caption" sx={{ background: "#d32f2f", px: 1.5, py: 0.5, borderRadius: 1 }}>
                {fieldError || submitError}
              </Typography>
            )}

            {isLast ? (
              <Button onClick={goNext} disabled={submitting || checking} endIcon={<CheckIcon />}
                sx={{ color: "#fff", fontWeight: 600 }}>
                {submitting ? "Enviando..." : "Enviar"}
              </Button>
            ) : (
              <Button onClick={goNext} disabled={checking} endIcon={<ArrowForwardIcon />} sx={{ color: "#fff", fontWeight: 600 }}>
                {checking ? "Verificando..." : (field.buttonLabel || "Próximo")}
              </Button>
            )}
          </Box>
        </Paper>
      </Box>

      <Box sx={{ textAlign: "center", py: 2, color: "#7a8aa8" }}>
        <Typography variant="caption">{idx + 1} de {sequence.length}</Typography>
      </Box>
    </Box>
  );
}
